import { MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/intake/StepContact";
import { BigSelect } from "@/components/intake/StepProduct";

function AddonPicker({ addons, selected, onToggle, error }) {
  return (
    <Field label="Which add-on(s) did you not receive? Tick all that apply" error={error} testId="field-addons">
      <div className="grid gap-2 sm:grid-cols-2" data-testid="addon-multiselect">
        {addons.map((a) => {
          const on = selected.includes(a);
          return (
            <label
              key={a}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 px-4 py-3 text-base font-semibold transition-colors ${on ? "border-black bg-[#FEF9C3]" : "border-zinc-300 bg-white hover:border-black"}`}
            >
              <Checkbox checked={on} onCheckedChange={() => onToggle(a)} data-testid={`addon-checkbox-${addons.indexOf(a)}`} className="h-5 w-5 border-2 border-black data-[state=checked]:bg-black" />
              {a}
            </label>
          );
        })}
      </div>
    </Field>
  );
}

export function StepQuery({ form, update, errors, config }) {
  const isChallenge = form.product === config.challenge_product;
  const showAddons = form.product === config.addon_product && form.query === config.addon_query;
  const toggleAddon = (a) => update({ addons: form.addons.includes(a) ? form.addons.filter((x) => x !== a) : [...form.addons, a] });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{form.product}</p>
        <h2 className="mt-1 font-display text-2xl font-bold">Step 3 · Tell us your query</h2>
      </div>

      {isChallenge && (
        <>
          <Field label="Which batch are you in?" error={errors.batch_mode} testId="field-batch-mode">
            <BigSelect
              value={form.batch_mode === "known" ? "I know my batch code" : form.batch_mode === "unknown" ? config.batch_unknown : ""}
              onChange={(v) => update({ batch_mode: v === config.batch_unknown ? "unknown" : "known", batch: "" })}
              options={["I know my batch code", config.batch_unknown]}
              placeholder="Select one…"
              testId="batch-mode-select"
            />
          </Field>
          {form.batch_mode === "known" && (
            <Field label="Your batch code" error={errors.batch} hint={config.batch_hint} testId="field-batch">
              <input
                className="field-input font-mono uppercase"
                placeholder="e.g. C10-B1 or B12"
                value={form.batch}
                onChange={(e) => update({ batch: e.target.value })}
                data-testid="batch-code-input"
              />
            </Field>
          )}
        </>
      )}

      <Field label="Select your query" error={errors.query} testId="field-query">
        <BigSelect
          value={form.query}
          onChange={(v) => update({ query: v, addons: [] })}
          options={config.queries[form.product] || []}
          placeholder="Select one…"
          testId="query-select"
        />
      </Field>

      {showAddons && <AddonPicker addons={config.addons} selected={form.addons} onToggle={toggleAddon} error={errors.addons} />}

      <label
        className={`flex cursor-pointer items-start gap-4 rounded-2xl border-[3px] border-black p-5 transition-colors brut-shadow ${form.offline_interest ? "bg-[#FACC15]" : "bg-[#FEF9C3] hover:bg-[#FEF08A]"}`}
        data-testid="indore-offline-callout"
      >
        <Checkbox
          checked={form.offline_interest}
          onCheckedChange={(v) => update({ offline_interest: Boolean(v) })}
          data-testid="indore-offline-checkbox"
          className="mt-0.5 h-7 w-7 rounded-md border-[3px] border-black data-[state=checked]:bg-black"
        />
        <span>
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em]">
            <MapPin className="h-4 w-4" /> Optional
          </span>
          <span className="mt-1 block font-display text-lg font-bold leading-snug sm:text-xl">{config.offline_label}</span>
          <span className="mt-1 block text-sm text-zinc-800">Tick this and our Indore team will reach out too. It does not change your query above.</span>
        </span>
      </label>
    </div>
  );
}
